import { useEffect, useState } from "react";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import { useProjectStore } from "../../store/useProjectStore";
import { useClientStore } from "../../store/useClientStore";
import { useAuthStore } from "../../store/useAuthStore";
import { useNavigate } from "react-router-dom";

import { Pencil, Trash2, Loader2, Eye, X , Link} from "lucide-react";

interface QuestionnaireListProps {
  setEditing: (q: any) => void;
}

const QuestionnaireList = ({ setEditing }: QuestionnaireListProps) => {
  const {
    questionnaires,
    isFetching,
    isDeleting,
    getAllQuestionnaires,
    deleteQuestionnaire
  } = useQuestionnaireStore() as any;
const navigate = useNavigate();

  const { fetchProjects } = useProjectStore();
  const { fetchClients } = useClientStore();
  const { authUser } = useAuthStore();
  // Only admins/managers need to see who authored each questionnaire.
  const showCreator = authUser?.role !== "Business Developer";

  useEffect(() => {
    fetchProjects();
    fetchClients();
    getAllQuestionnaires();
  }, []);

  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="animate-spin text-[#2462b2]" size={40} />
        <p className="text-gray-500 font-medium">Loading questionnaires...</p>
      </div>
    );
  }

  if (!questionnaires.length) {
    return (
      <div className="text-center py-16">
        <div className="bg-gray-100 border-2 border-dashed rounded-xl w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <svg
            className="w-10 h-10 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-700">No questionnaires yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Create your first questionnaire to get started!
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-t-2xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gradient-to-r from-[#2462b2]/5 to-[#1e55a0]/5 text-xs font-bold uppercase tracking-wider text-gray-700 border-b border-gray-200">
            <tr>
              {[
                "Name",
                "Description",
                "Service Type",
                "Project",
                "Contact",
                "Status",
                "Submitted At",
                ...(showCreator ? ["Created By"] : []),
                "Actions",
              ].map((header) => (
                <th key={header} className="px-5 py-4 font-semibold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {questionnaires.map((q: any) => (
              <tr
                key={q._id}
                className="hover:bg-gradient-to-r hover:from-[#2462b2]/5 hover:to-transparent transition-all duration-200 group"
              >
                <td className="px-5 py-4 font-semibold text-gray-900">{q.name}</td>
                <td className="px-5 py-4 font-semibold text-gray-900">{q.description}</td>
                <td className="px-5 py-4 font-semibold text-gray-900">{q.serviceType}</td>
                <td className="px-5 py-4">{q.projectId?.name || "—"}</td>
                <td className="px-5 py-4">{q.clientId?.name || "—"}</td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap transition-all duration-200 ${
                      q.isSubmitted
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {q.isSubmitted ? "Submitted" : "Pending"}
                  </span>
                </td>
              <td className="px-5 py-4">
  {q.submittedAt
    ? new Date(q.submittedAt).toISOString().split("T")[0]
    : "—"}
</td>

                {showCreator && (
                  <td className="px-5 py-4">
                    {q.createdBy?.fullName ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-1 ring-1 ring-primary/15">
                        <span className="grid place-items-center w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold">
                          {q.createdBy.fullName.charAt(0).toUpperCase()}
                        </span>
                        <span className="text-[12px] font-semibold text-gray-700">{q.createdBy.fullName}</span>
                      </span>
                    ) : "—"}
                  </td>
                )}

                <td className="px-5 py-4">
                  <div className="flex items-center justify-center gap-2">
  <button
    onClick={() => navigate(`/questionnaires/${q._id}`)}
    className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 group"
    title="View Questions"
  >
    <Eye size={16} className="group-hover:scale-110 transition-transform" />
  </button>

  <button
    onClick={() => navigate(`/questionnaires/${q._id}/edit`)}

    className={`p-2 rounded-lg bg-blue-50 text-blue-700 transition-all duration-200 group `}
    title={q.isSubmitted ? "Cannot edit a submitted questionnaire" : "Edit"}
  >
    <Pencil size={16} className="group-hover:scale-110 transition-transform" />
  </button>

  <button
    onClick={() => {
      if (window.confirm("Are you sure you want to delete this questionnaire?")) {
        deleteQuestionnaire(q._id);
      }
    }}
    disabled={isDeleting}
    className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 transition-all duration-200 group disabled:opacity-50"
    title="Delete"
  >
    {isDeleting ? (
      <Loader2 size={16} className="animate-spin" />
    ) : (
      <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
    )}
  </button>

  <button
    onClick={() => {
      const url = (q as any).shareUrl || `${window.location.origin}/questionnaire/public/${q.questionnaireToken}`;
      navigator.clipboard.writeText(url);
      alert("Questionnaire link copied!");
    }}
    disabled={q.isSubmitted}
    className={`p-2 rounded-lg bg-green-50 text-green-700 transition-all duration-200 group ${
      q.isSubmitted ? "opacity-50 cursor-not-allowed" : "hover:bg-green-100"
    }`}
    title={q.isSubmitted ? "Cannot copy link of a submitted questionnaire" : "Copy Link"}
  >
    <Link size={16} className="group-hover:scale-110 transition-transform" />
  </button>
</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </>
  );
};

export default QuestionnaireList;
